import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Download, FileText, IndianRupee, Wallet, TrendingUp } from "lucide-react";
import { inr } from "@/lib/utils";
import MetricCard from "@/components/MetricCard";

/**
 * iter58 — Lifetime Earnings / Staff Payout Summary.
 *
 * Consolidated per-staff roll-up: Shoot Ledger + Weekly Payroll → combined
 * lifetime earnings + total paid vs outstanding. CSV and print-PDF export.
 * Admin-only (mounted inside /team tab; the endpoint is `require_admin`).
 */
export default function LifetimeEarnings() {
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/staff/earnings-summary");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load lifetime earnings");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Sort UI — default by combined_lifetime desc, but let admin resort.
  const [sortKey, setSortKey] = useState("combined_lifetime");
  const [sortDir, setSortDir] = useState("desc");
  const sortedRows = useMemo(() => {
    const rows = [...(data.rows || [])];
    rows.sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const numeric = typeof av === "number" && typeof bv === "number";
      const cmp = numeric ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [data.rows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const downloadCsv = () => {
    // iter60 — 6-column CSV per user spec: separates Shoot Dues from
    // Weekly Salary Paid, with a single "Total Lifetime Studio Payout"
    // column at the end (= shoot_paid + weekly_paid).
    const headers = ["Staff", "Total Shoots", "Shoot Earnings", "Shoot Outstanding Due", "Weekly Salary Paid", "Total Lifetime Studio Payout"];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(escape).join(",")];
    for (const r of sortedRows) {
      lines.push([
        r.name, r.shoot_count,
        r.shoot_earnings, r.shoot_outstanding, r.weekly_paid, r.total_paid,
      ].map(escape).join(","));
    }
    const t = data.totals || {};
    lines.push([
      "TOTALS", "",
      t.shoot_earnings || 0, t.outstanding_due || 0, t.weekly_paid || 0, t.total_paid || 0,
    ].map(escape).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kahani-lifetime-earnings-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  // PDF export = printable window with editorial styling. Native browser
  // "Save as PDF" produces a clean 1-page owner-friendly summary without
  // pulling in another dependency (jsPDF etc.).
  const downloadPdf = () => {
    const t = data.totals || {};
    const inrFmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);
    const now = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) { toast.error("Popup blocked — allow popups to export PDF"); return; }
    const rowsHtml = sortedRows.map((r) => `
      <tr>
        <td>${r.name}</td>
        <td class="num">${r.shoot_count}</td>
        <td class="num">${inrFmt(r.shoot_earnings)}</td>
        <td class="num ${r.shoot_outstanding > 0 ? "due" : ""}">${inrFmt(r.shoot_outstanding)}</td>
        <td class="num paid">${inrFmt(r.weekly_paid)}</td>
        <td class="num strong">${inrFmt(r.total_paid)}</td>
      </tr>`).join("");
    w.document.write(`<!DOCTYPE html><html><head>
      <title>Kahani Studio · Lifetime Earnings Summary · ${now}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600&family=IBM+Plex+Sans:wght@400;500;600&display=swap');
        body { font-family: 'IBM Plex Sans', sans-serif; margin: 40px; color: #1C1917; background: #F9F8F6; }
        .eyebrow { font-size: 10px; letter-spacing: 0.24em; text-transform: uppercase; color: #78716C; font-weight: 600; }
        h1 { font-family: 'Playfair Display', serif; font-size: 32px; letter-spacing: -0.02em; margin: 4px 0 4px; font-weight: 600; }
        .meta { color: #78716C; font-size: 12px; margin-bottom: 24px; }
        hr { border: 0; border-top: 1px solid #E6E2D8; margin: 20px 0; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { padding: 10px 8px; border-bottom: 1px solid #E6E2D8; text-align: left; }
        th { font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: #78716C; font-weight: 600; }
        td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; font-family: 'IBM Plex Sans', monospace; }
        td.strong { font-weight: 600; color: #4A1521; }
        td.paid { color: #047857; }
        td.due { color: #B91C1C; font-weight: 600; }
        tfoot td { border-top: 2px solid #4A1521; border-bottom: 0; padding-top: 14px; font-weight: 600; font-size: 13px; }
        .note { color: #78716C; font-size: 10px; font-style: italic; margin-top: 8px; }
        .footer { color: #78716C; font-size: 10px; margin-top: 32px; }
      </style></head><body>
      <div class="eyebrow">Kahani Studio · Finance</div>
      <h1>Lifetime Earnings Summary</h1>
      <div class="meta">Shoot Dues + Weekly Office Salary · Prepared ${now}</div>
      <hr />
      <table>
        <thead><tr>
          <th>Staff</th>
          <th class="num">Total Shoots</th>
          <th class="num">Shoot Earnings</th>
          <th class="num">Shoot Outstanding Due</th>
          <th class="num">Weekly Salary Paid</th>
          <th class="num">Total Lifetime Payout</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr>
          <td>TOTAL</td><td></td>
          <td class="num">${inrFmt(t.shoot_earnings)}</td>
          <td class="num ${(t.outstanding_due || 0) > 0 ? "due" : ""}">${inrFmt(t.outstanding_due)}</td>
          <td class="num paid">${inrFmt(t.weekly_paid)}</td>
          <td class="num strong">${inrFmt(t.total_paid)}</td>
        </tr></tfoot>
      </table>
      <div class="note">Shoot Dues and Weekly Salary are two independent financial flows. Outstanding Due reflects only unpaid shoot amounts and never deducts weekly salary.</div>
      <div class="footer">Generated by Kahani Studio Manager · Confidential — for internal accounting only.</div>
      <script>window.onload = () => { window.print(); }; </script>
    </body></html>`);
    w.document.close();
    toast.success("Opening print dialog — pick 'Save as PDF'");
  };

  return (
    <div className="space-y-5 fade-in" data-testid="lifetime-earnings-page">
      {/* Editorial hero-lite */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="editorial-eyebrow">Kahani Studio · Finance</div>
          <div className="editorial-h2 mt-1">Lifetime Earnings Summary</div>
      <p className="editorial-sub mt-1">Shoot Dues and Weekly Office Salary are independent financial flows — Outstanding Due reflects unpaid shoots only.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-outline" onClick={downloadCsv} disabled={loading} data-testid="le-export-csv">
            <Download className="w-4 h-4" strokeWidth={1.6} /> Download CSV
          </button>
          <button className="btn btn-primary" onClick={downloadPdf} disabled={loading} data-testid="le-export-pdf">
            <FileText className="w-4 h-4" strokeWidth={1.6} /> PDF Summary
          </button>
        </div>
      </div>

      {/* Summary MetricCards — iter60 layout: Total Payout · Shoot Earnings · Salary Paid · Shoot Outstanding */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          testid="le-metric-payout"
          eyebrow="Total Lifetime Payout"
          value={inr(data.totals.total_paid || 0)}
          subtitle="Shoot Paid + Salary Paid"
          icon={Wallet}
          tone="wine"
          loading={loading}
        />
        <MetricCard
          testid="le-metric-shoot"
          eyebrow="Total Shoot Earnings"
          value={inr(data.totals.shoot_earnings || 0)}
          subtitle="Gross earned from shoots"
          icon={IndianRupee}
          tone="amber"
          loading={loading}
        />
        <MetricCard
          testid="le-metric-weekly"
          eyebrow="Weekly Salary Paid"
          value={inr(data.totals.weekly_paid || 0)}
          subtitle="Office staff wages settled"
          icon={TrendingUp}
          tone="emerald"
          loading={loading}
        />
        <MetricCard
          testid="le-metric-due"
          eyebrow="Shoot Outstanding Due"
          value={inr(data.totals.outstanding_due || 0)}
          subtitle="Unpaid shoot balances only"
          icon={IndianRupee}
          tone={(data.totals.outstanding_due || 0) > 0 ? "red" : "neutral"}
          loading={loading}
        />
      </div>

      {/* Table */}
      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="le-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold text-left">
                <SortableTh label="Staff Name" k="name" cur={sortKey} dir={sortDir} on={handleSort} />
                <SortableTh label="Total Shoots" k="shoot_count" cur={sortKey} dir={sortDir} on={handleSort} num />
                <SortableTh label="Shoot Earnings" k="shoot_earnings" cur={sortKey} dir={sortDir} on={handleSort} num tip="Gross earned from all assigned shoot rows (day rate × shoots + bonuses / overrides). Independent of weekly salary." />
                <SortableTh label="Shoot Outstanding Due" k="shoot_outstanding" cur={sortKey} dir={sortDir} on={handleSort} num tip="Unpaid shoot balance only: Shoot Earnings − Shoot Paid. Weekly salary is NEVER deducted from this figure." />
                <SortableTh label="Weekly Salary Paid" k="weekly_paid" cur={sortKey} dir={sortDir} on={handleSort} num tip="Office wages settled via Weekly Payroll (Sun–Sat cycle). A separate financial flow — does not affect Shoot Dues." />
                <SortableTh label="Total Lifetime Studio Payout" k="total_paid" cur={sortKey} dir={sortDir} on={handleSort} num tip="Overall money actually paid out to this staff member: Shoot Paid + Weekly Salary Paid." />
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading ? (
                <tr><td colSpan={6} className="py-16 text-center text-stone-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : sortedRows.length === 0 ? (
                <tr><td colSpan={6} className="py-16 text-center text-stone-400 text-xs">No active staff yet. Add a staff member from the Roster tab first.</td></tr>
              ) : (
                sortedRows.map((r) => (
                  <tr key={r.staff_id} data-testid={`le-row-${r.staff_id}`} className="hover:bg-stone-50/50">
                    <td className="px-3 py-3 font-medium text-stone-800">{r.name}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-stone-500">{r.shoot_count}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-stone-700">{inr(r.shoot_earnings)}</td>
                    <td className={`px-3 py-3 text-right tabular-nums font-semibold ${r.shoot_outstanding > 0 ? "text-red-700" : "text-stone-400"}`}>
                      {inr(r.shoot_outstanding)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-emerald-700">{inr(r.weekly_paid)}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold text-rose-800">{inr(r.total_paid)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {sortedRows.length > 0 && (
              <tfoot className="bg-stone-50 border-t-2 border-stone-200">
                <tr className="font-semibold" data-testid="le-totals-row">
                  <td className="px-3 py-3 text-[11px] uppercase tracking-widest text-stone-600">Totals</td>
                  <td />
                  <td className="px-3 py-3 text-right tabular-nums text-stone-800">{inr(data.totals.shoot_earnings || 0)}</td>
                  <td className={`px-3 py-3 text-right tabular-nums ${(data.totals.outstanding_due || 0) > 0 ? "text-red-800" : "text-stone-500"}`}>
                    {inr(data.totals.outstanding_due || 0)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums text-emerald-800">{inr(data.totals.weekly_paid || 0)}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-heading text-base text-rose-800">{inr(data.totals.total_paid || 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function SortableTh({ label, k, cur, dir, on, num, tip }) {
  const active = cur === k;
  return (
    <th
      onClick={() => on(k)}
      title={tip || ""}
      className={`px-3 py-3 cursor-pointer select-none whitespace-nowrap ${num ? "text-right" : "text-left"} ${active ? "text-rose-800" : "hover:text-stone-800"}`}
    >
      {label}
      <span className="ml-1 opacity-70">{active ? (dir === "asc" ? "↑" : "↓") : ""}</span>
    </th>
  );
}
